import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Checkbox,
  Icon,
  Form,
} from 'semantic-ui-react';
import { showWarning } from '../helpers';

// 与后端 relay/billing/pricing/pricing.go 的 normalizeModelName 保持一致。
// 后端在保存与查询时都会归一化，界面前置做同样的处理可让「所见 = 实际存储」。
// ⚠️ 这是有意为之的规则重复：若前端不归一化，运营填入带空白或全角连字符的名字时，
// 重复检查会放过、后端却会整份拒绝。改动此处必须同步改 Go 侧。
const DASH_VARIANTS = [
  '‐', // hyphen
  '‑', // non-breaking hyphen
  '‒', // figure dash
  '–', // en dash
  '—', // em dash
  '―', // horizontal bar
  '−', // minus sign
  '－', // fullwidth hyphen-minus
];

function normalizeModelName(name) {
  let s = String(name == null ? '' : name).trim();
  DASH_VARIANTS.forEach((d) => {
    s = s.split(d).join('-');
  });
  return s.trim().toLowerCase();
}

const DEFAULT_CONFIG = {
  models: {
    'qwen3-embedding-8b': { peak_input: 0.5, peak_output: 0.5, idle_input: 0.25, idle_output: 0.25, cache_supported: false },
    'qwen3.6-27b': { peak_input: 1, peak_output: 3, idle_input: 0.5, idle_output: 1.5, cache_supported: true },
    'qwen3.6-122b-a10b': { peak_input: 1, peak_output: 4, idle_input: 0.5, idle_output: 2, cache_supported: true },
    'deepseek-v4-flash': { peak_input: 2, peak_output: 7, idle_input: 1, idle_output: 3, cache_supported: true },
    'glm-5.2': { peak_input: 6.5, peak_output: 23, idle_input: 5, idle_output: 18, cache_supported: true },
    'aic-cloud-call-asr': { peak_input: 0, peak_output: 630, idle_input: 0, idle_output: 315, cache_supported: false },
    'baic-badge-asr': { peak_input: 0, peak_output: 980, idle_input: 0, idle_output: 490, cache_supported: false },
  },
  default: { peak_input: 1, peak_output: 3, idle_input: 0.5, idle_output: 1.5, cache_supported: true },
  peak_start_hour: 8,
  peak_end_hour: 20,
  cache_hit_ratio: 0.2,
};

// Parse the JSON string from the Option store into a workable object.
function parseConfig(jsonStr) {
  if (!jsonStr || jsonStr.trim() === '') {
    return JSON.parse(JSON.stringify(DEFAULT_CONFIG));
  }
  try {
    const cfg = JSON.parse(jsonStr);
    if (!cfg.models) cfg.models = {};
    if (!cfg.default) {
      cfg.default = { ...DEFAULT_CONFIG.default };
    }
    if (cfg.peak_start_hour === undefined) cfg.peak_start_hour = 8;
    if (cfg.peak_end_hour === undefined) cfg.peak_end_hour = 20;
    if (cfg.cache_hit_ratio === undefined) cfg.cache_hit_ratio = 0.2;
    return cfg;
  } catch {
    return JSON.parse(JSON.stringify(DEFAULT_CONFIG));
  }
}

// Turn the config object back into a pretty-printed JSON string.
function serializeConfig(cfg) {
  return JSON.stringify(cfg, null, 2);
}

const ModelPricingTable = ({ value, onChange }) => {
  const [cfg, setCfg] = useState(() => parseConfig(value));
  // Draft name for the model row currently being edited, so renaming a model
  // doesn't reorder/sort the table on every keystroke. Sorting is deferred to blur.
  const [draftName, setDraftName] = useState(null);

  // Re-parse when the external value changes (e.g. after initial load).
  useEffect(() => {
    setCfg(parseConfig(value));
  }, [value]);

  // Notify parent of every change so submitConfig can diff and save.
  const updateCfg = (next) => {
    setCfg(next);
    onChange(serializeConfig(next));
  };

  // --- Global parameter handlers ---
  const handleGlobalChange = (field, val) => {
    updateCfg({ ...cfg, [field]: val });
  };

  // --- Model row handlers ---
  const handleModelChange = (name, field, val) => {
    const models = { ...cfg.models };
    models[name] = { ...models[name], [field]: val };
    updateCfg({ ...cfg, models });
  };

  const handleDefaultChange = (field, val) => {
    updateCfg({ ...cfg, default: { ...cfg.default, [field]: val } });
  };

  const addModel = () => {
    // 不能用「已有条目数 + 1」：删掉一行后计数会回退，撞上已存在的
    // new-model-N，导致该条定价被静默覆盖。这里取第一个未被占用的编号。
    let n = Object.keys(cfg.models).length + 1;
    while (cfg.models['new-model-' + n] !== undefined) {
      n += 1;
    }
    const name = 'new-model-' + n;
    const models = {
      ...cfg.models,
      [name]: { peak_input: 1, peak_output: 3, idle_input: 0.5, idle_output: 1.5, cache_supported: true },
    };
    updateCfg({ ...cfg, models });
  };

  const deleteModel = (name) => {
    const models = { ...cfg.models };
    delete models[name];
    updateCfg({ ...cfg, models });
  };

  // --- Model name handlers (rename committed on blur, not on every keystroke) ---
  const handleModelNameChange = (oldName, newName) => {
    setDraftName({ oldName, value: newName });
  };

  const handleModelNameBlur = (oldName) => {
    const raw =
      draftName && draftName.oldName === oldName ? draftName.value : oldName;
    setDraftName(null);
    // 后端保存时会归一化，界面前置同样处理，避免「填的和存的不一样」
    const newName = normalizeModelName(raw);

    if (!newName || newName === oldName) return;
    // 仅大小写 / 空白变化：后端本就会归一化成同一个键，无需动本地 key
    if (normalizeModelName(oldName) === newName) return;
    // 重复检查必须在归一化之后做，否则会造出后端必然整份拒绝的状态
    if (cfg.models[newName] !== undefined) {
      showWarning(`模型名「${newName}」已存在，未重命名`);
      return;
    }

    const models = { ...cfg.models };
    const p = models[oldName];
    delete models[oldName];
    models[newName] = p;
    updateCfg({ ...cfg, models });
  };

  const modelNames = Object.keys(cfg.models).sort();

  const renderRow = (name, pricing, isDefault) => {
    const handleChange = (field, val) => {
      if (isDefault) {
        handleDefaultChange(field, val);
      } else {
        handleModelChange(name, field, val);
      }
    };

    return (
      <Table.Row key={isDefault ? '__default__' : name}>
        <Table.Cell>
          {isDefault ? (
            <strong style={{ color: '#888' }}>*（默认）</strong>
          ) : (
            <Input
              value={
                draftName && draftName.oldName === name
                  ? draftName.value
                  : name
              }
              transparent
              fluid
              onChange={(e) => handleModelNameChange(name, e.target.value)}
              onBlur={() => handleModelNameBlur(name)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.target.blur();
              }}
            />
          )}
        </Table.Cell>
        <Table.Cell>
          <Input
            type='number'
            step='0.01'
            value={pricing.peak_input}
            transparent
            fluid
            onChange={(e) => handleChange('peak_input', parseFloat(e.target.value) || 0)}
          />
        </Table.Cell>
        <Table.Cell>
          <Input
            type='number'
            step='0.01'
            value={pricing.peak_output}
            transparent
            fluid
            onChange={(e) => handleChange('peak_output', parseFloat(e.target.value) || 0)}
          />
        </Table.Cell>
        <Table.Cell>
          <Input
            type='number'
            step='0.01'
            value={pricing.idle_input}
            transparent
            fluid
            onChange={(e) => handleChange('idle_input', parseFloat(e.target.value) || 0)}
          />
        </Table.Cell>
        <Table.Cell>
          <Input
            type='number'
            step='0.01'
            value={pricing.idle_output}
            transparent
            fluid
            onChange={(e) => handleChange('idle_output', parseFloat(e.target.value) || 0)}
          />
        </Table.Cell>
        <Table.Cell textAlign='center'>
          <Checkbox
            checked={pricing.cache_supported}
            onChange={(e, { checked }) => handleChange('cache_supported', checked)}
          />
        </Table.Cell>
        <Table.Cell textAlign='center'>
          {!isDefault && (
            <Button
              icon
              color='red'
              size='mini'
              basic
              onClick={() => deleteModel(name)}
            >
              <Icon name='trash' />
            </Button>
          )}
        </Table.Cell>
      </Table.Row>
    );
  };

  return (
    <div>
      <Form.Group widths='equal'>
        <Form.Input
          label='高峰开始时段（小时）'
          type='number'
          min='0'
          max='23'
          value={cfg.peak_start_hour}
          onChange={(e) => handleGlobalChange('peak_start_hour', parseInt(e.target.value) || 0)}
        />
        <Form.Input
          label='高峰结束时段（小时）'
          type='number'
          min='0'
          max='23'
          value={cfg.peak_end_hour}
          onChange={(e) => handleGlobalChange('peak_end_hour', parseInt(e.target.value) || 0)}
        />
        <Form.Input
          label='缓存命中折扣（0-1）'
          type='number'
          step='0.01'
          min='0'
          max='1'
          value={cfg.cache_hit_ratio}
          onChange={(e) => handleGlobalChange('cache_hit_ratio', parseFloat(e.target.value) || 0)}
        />
      </Form.Group>

      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>模型名称</Table.HeaderCell>
            <Table.HeaderCell>高峰输入 (元/百万)</Table.HeaderCell>
            <Table.HeaderCell>高峰输出 (元/百万)</Table.HeaderCell>
            <Table.HeaderCell>空闲输入 (元/百万)</Table.HeaderCell>
            <Table.HeaderCell>空闲输出 (元/百万)</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>支持缓存</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {modelNames.map((name) =>
            renderRow(name, cfg.models[name], false)
          )}
          {renderRow('*', cfg.default, true)}
        </Table.Body>
      </Table>

      <Button
        icon
        labelPosition='left'
        primary
        size='small'
        onClick={addModel}
      >
        <Icon name='plus' />
        添加模型
      </Button>
    </div>
  );
};

export default ModelPricingTable;
